"use client";

import { useLingui } from "@lingui/react/macro";
import { PlusIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { type AnalysisTarget } from "@/lib/ai/analysis-target";
import { AnalysisTargetCommandGroups } from "./analysis-target-command-groups";
import {
  type AnalysisTargetOptionsState,
  filterAnalysisTargetOptionGroups,
} from "./analysis-target-options";

type Props = {
  optionsState: AnalysisTargetOptionsState;
  selectedTargets: AnalysisTarget[];
  onSelectTarget: (target: AnalysisTarget) => void;
  disabled?: boolean;
  onOpenChange?: (open: boolean) => void;
};

/**
 * `+` ボタンから開く分析対象（project/view）選択ポップアップ。
 */
export function AnalysisTargetPicker({
  optionsState,
  selectedTargets,
  onSelectTarget,
  disabled,
  onOpenChange,
}: Props) {
  const { i18n, t } = useLingui();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        setQuery("");
      }
      setOpen(nextOpen);
      onOpenChange?.(nextOpen);
    },
    [onOpenChange],
  );
  /**
   * 候補一覧では current page / selected target も隠さず見せる。
   *
   * duplicate add の抑止は state 側の no-op に任せ、UI は「何が存在するか」を
   * そのまま反映することで、現在ページを探したときに消えて見える違和感を防ぐ。
   */
  const filteredGroups = useMemo(
    () =>
      filterAnalysisTargetOptionGroups(optionsState.groupedOptions, query, {
        locale: i18n.locale,
      }),
    [i18n.locale, optionsState.groupedOptions, query],
  );

  useEffect(() => {
    if (disabled && open) {
      handleOpenChange(false);
    }
  }, [disabled, handleOpenChange, open]);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                disabled={disabled}
                className="rounded-full text-muted-foreground hover:text-foreground"
                aria-label={t`Add context`}
              >
                <PlusIcon className="size-4" />
              </Button>
            </PopoverTrigger>
          </span>
        </TooltipTrigger>
        <TooltipContent side="top">{t`Add context`}</TooltipContent>
      </Tooltip>
      <PopoverContent className="w-[340px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder={t`Search project or view`}
          />
          <CommandList>
            <CommandEmpty>
              {optionsState.error
                ? t`Failed to load targets`
                : t`No matching project or view`}
            </CommandEmpty>
            {optionsState.fetching ? (
              <CommandGroup heading={t`Loading`}>
                <CommandItem value="loading" disabled>
                  {t`Loading targets...`}
                </CommandItem>
              </CommandGroup>
            ) : null}
            <AnalysisTargetCommandGroups
              groupedOptions={filteredGroups}
              selectedTargets={selectedTargets}
              onSelectTarget={(target) => {
                onSelectTarget(target);
                handleOpenChange(false);
              }}
            />
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
